import { ENDPOINT } from '~/constants';

import { LOADING_STATE } from '../constants/loadingState';
import DataExchange from '../models/DataExchange';
import DataExchangeCompany from '../models/DataExchangeCompany';
import {
  replaceDataExchangeCompanies,
  replaceDataExchanges,
  setDataExchangeCompaniesLoading,
  setDataExchangesLoading,
} from '../redux/dataExchangesSlice';
import store from '../redux/store';
import { promiseHandler } from '../utils/promiseHandler';

import { vestigasApi } from './kyClient';
import ToastService from './toast.service';

class DataExchangeService {
  async getAllDataExchangeCompanies(supplierAlertConfigs) {
    return vestigasApi
      .get(ENDPOINT.DATA_EXCHANGES.COMPANIES.GET_ALL())
      .json()
      .then((data) => {
        return data.items.map((item) => {
          const dataExchangeCompany = new DataExchangeCompany();
          dataExchangeCompany.init(item, supplierAlertConfigs);

          return dataExchangeCompany;
        });
      })
      .catch((error) => {
        ToastService.httpError(
          ['Anbindungen konnten nicht geladen werden.'],
          error.response,
        );
        throw error;
      });
  }

  async getAllDataExchanges() {
    return vestigasApi
      .get(ENDPOINT.DATA_EXCHANGES.GET_ALL())
      .json()
      .then((data) => {
        return data.items.map((item) => {
          const dataExchange = new DataExchange();
          dataExchange.init(item);

          return dataExchange;
        });
      })
      .catch((error) => {
        ToastService.httpError(
          ['Datenaustausch-Einträge konnten nicht geladen werden.'],
          error.response,
        );
        throw error;
      });
  }

  async updateDataExchangeCompany(body) {
    return vestigasApi
      .put(ENDPOINT.DATA_EXCHANGES.COMPANIES.UPDATE(), { json: body })
      .catch((error) => {
        ToastService.httpError(
          ['Anbindung konnte nicht aktualisiert werden.'],
          error.response,
        );
        throw error;
      });
  }

  async updateDataExchange(body) {
    return vestigasApi
      .put(ENDPOINT.DATA_EXCHANGES.UPDATE(), { json: body })
      .catch((error) => {
        if (error?.response?.status === 409) {
          ToastService.error(
            resolveVestigasHttpErrorDetail(error) || error.message,
          );
          throw error;
        }

        ToastService.httpError(
          ['Datenaustausch-Eintrag konnte nicht aktualisiert werden.'],
          error.response,
        );
        throw error;
      });
  }

  async deleteDataExchange(dataExchangeId) {
    return vestigasApi
      .delete(ENDPOINT.DATA_EXCHANGES.DELETE(dataExchangeId))
      .catch((error) => {
        ToastService.httpError(
          ['Datenaustausch-Eintrag konnte nicht gelöscht werden.'],
          error.response,
        );
        throw error;
      });
  }

  async loadDataExchangeCompanies() {
    // to not load data exchange companies again when they are already loading or have already been loaded
    if (
      store.getState().dataExchanges?.dataExchangeCompaniesLoading !==
      LOADING_STATE.NOT_LOADED
    ) {
      return;
    }

    this.refreshDataExchangeCompanies();
  }

  async refreshDataExchangeCompanies() {
    store.dispatch(setDataExchangeCompaniesLoading(LOADING_STATE.LOADING));

    const supplierAlertConfigs =
      store.getState().companies?.supplierAlertConfigs;

    const [dataExchangeCompanies, error] = await promiseHandler(
      this.getAllDataExchangeCompanies(supplierAlertConfigs),
    );

    if (error) {
      store.dispatch(setDataExchangeCompaniesLoading(LOADING_STATE.FAILED));
      return;
    }

    store.dispatch(replaceDataExchangeCompanies(dataExchangeCompanies));
  }

  async loadDataExchanges() {
    // to not load data exchanges again when they are already loading or have already been loaded
    if (
      store.getState().dataExchanges?.dataExchangesLoading !==
      LOADING_STATE.NOT_LOADED
    ) {
      return;
    }

    this.refreshDataExchanges();
  }

  async refreshDataExchanges() {
    store.dispatch(setDataExchangesLoading(LOADING_STATE.LOADING));

    const [dataExchanges, error] = await promiseHandler(
      this.getAllDataExchanges(),
    );

    if (error) {
      store.dispatch(setDataExchangesLoading(LOADING_STATE.FAILED));
      return;
    }

    store.dispatch(replaceDataExchanges(dataExchanges));
  }
}

export default new DataExchangeService();
